import type { Case, CaseComment, CaseFile, CaseFileType } from '../types/case'

/** True when the case has a procedure_date and that date is strictly in the past. */
export function hasProcedureDatePassed(
  procedureDate: Case['procedure_date'],
): boolean {
  if (!procedureDate) {
    return false
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const procedureDay = new Date(`${procedureDate}T00:00:00`)
  return procedureDay < today
}

/**
 * Free-text case outcome (Edit) and structured Outcome tab (Detail) share this gate:
 * only when the case is published and the procedure date has passed.
 */
export function canEditCaseOutcome(
  caseItem: Pick<Case, 'status' | 'procedure_date'>,
): boolean {
  return caseItem.status === 'published' && hasProcedureDatePassed(caseItem.procedure_date)
}

/** Alias — same eligibility as Edit free-text outcome; used to enable the Outcome detail tab. */
export function canShowCaseOutcomeTab(
  caseItem: Pick<Case, 'status' | 'procedure_date'>,
): boolean {
  return canEditCaseOutcome(caseItem)
}

/** Stored clinical phase on the case — not derived from status or review flags. */
export function isCasePreOpPhase(caseItem: Pick<Case, 'phase'>): boolean {
  return caseItem.phase === 'pre_op'
}

export function isCasePostOpPhase(caseItem: Pick<Case, 'phase'>): boolean {
  return caseItem.phase === 'post_op'
}

/** New comments inherit the case's stored clinical phase. */
export function activeCommentTag(
  caseItem: Pick<Case, 'phase'>,
): 'pre_op' | 'post_op' {
  return caseItem.phase === 'post_op' ? 'post_op' : 'pre_op'
}

export function splitCommentsByTag(comments: CaseComment[]) {
  return {
    // Treat missing tag as pre-op so a rare realtime payload gap still renders.
    preOp: comments.filter((comment) => comment.tag !== 'post_op' && comment.tag !== 'national'),
    postOp: comments.filter((comment) => comment.tag === 'post_op'),
  }
}

export function groupFilesByType(files: CaseFile[]): Record<CaseFileType, CaseFile[]> {
  return {
    pre_op: files.filter((file) => file.type === 'pre_op'),
    post_op: files.filter((file) => file.type === 'post_op'),
    outcome: files.filter((file) => file.type === 'outcome'),
  }
}
